import { CKEditor } from '@ckeditor/ckeditor5-react'
import {
    ClassicEditor,
    Essentials,
    EventInfo,
    Paragraph,
    Undo,
} from 'ckeditor5'
import {
    Comments,
    SuggestionData,
    TrackChanges,
    TrackChangesData,
} from 'ckeditor5-premium-features'
import 'ckeditor5-premium-features/ckeditor5-premium-features.css'
import 'ckeditor5/ckeditor5.css'
import { useChatProfileStore } from 'components/AskDelaChat/ChatProfileStore'
import { processEnv } from 'components/configuration/environmentVariables'
import { useHtmlParagraphPaste } from 'components/WorkspaceEditor/features/copypaste/hooks/useHtmlParagraphPaste'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from 'components/WorkspaceEditor/features/keyOperations/store/NodeMapStore'
import { useHighlightStoreShallowSelector } from 'components/WorkspaceEditor/HighlightStore'
import React, { memo, useCallback, useMemo, useRef } from 'react'
import { useCursorPositioning } from '../NodeContents/PlainText/hooks/useCursorPositioning'
import { ContentEditorLayout } from './ContentEditorLayout'
import { hideInLineTrackTip, HIGHLIGHT_CLASS, setupEditorCharacterLimit } from './contentUtils'
import { TextIdPlugin, TrackChangePlugin, UserPlugin } from './model'
import { EmptyContentCaretPlugin } from './plugins/EmptyContentCaretPlugin'

// Timing constants for editor initialization
const EDITOR_CONFIG_DELAY = 50 // Prevent cursor positioning conflicts with focus
const FIREFOX_HIGHLIGHT_DELAY = 100 // Firefox-specific rendering delay

/**
 * A type of callback function to be called when the editor is ready
 * @param editor - The editor instance
 */
export type ClassicEditorOnReadyHandler = (editor: ClassicEditor) => void

/**
 * A type of callback function to be called when the editor loses focus
 */
export type ClassicEditorHandler = (
    /** The event information */
    event: EventInfo,
    /** The editor instance */
    editor: ClassicEditor
) => void

/** Props for the ContentEditor component */
export interface ContentEditorProps {
    /** Initial editor data in HTML format */
    initialData?: string
    /** Initial track changes (suggestions) to load into the editor (this is also used for collaborators configuration) */
    initialSuggestions?: SuggestionData[]
    /** Click coordinates for cursor positioning when editor activates */
    clickCoordinates?: { x: number; y: number }
    handlers?: {
        /** Callback function to be called when the editor is ready */
        onReady?: ClassicEditorOnReadyHandler
        /** Callback function to be called when the editor loses focus */
        onBlur?: ClassicEditorHandler
        /** Callback function to be called when the editor changes */
        onChange?: ClassicEditorHandler
    }
    /** Represent the flag to check to apply track changes in content editor  */
    enableTrackChange?: boolean
    /** Optional maximum character length. When set, prevents input beyond this limit. */
    maxLength?: number
}

// editor plugins
const EDITOR_PLUGINS = [
    Essentials,
    Paragraph,
    Undo,
    UserPlugin,
    Comments,
    TrackChanges,
    TrackChangePlugin,
    TrackChangesData,
    TextIdPlugin,
    EmptyContentCaretPlugin, // Handles caret visibility for empty content
]

// Comment Configs
const COMMENTS_CONFIG = {
    editorConfig: {
        extraPlugins: [Comments],
        sidebar: {
            container: document.body,
        },
    },
}

/**
 * A memoized ContentEditor component.
 * Create an instance of the ContentEditor component with overridden layout.
 * @param {ContentEditorProps} props - The props for the ContentEditor component.
 * @returns {React.ReactElement} Rendered ContentEditor component
 */
export const ContentEditor = memo<ContentEditorProps>(
    ({
        initialData = '<p>:smiley:</p>',
        initialSuggestions,
        clickCoordinates,
        handlers,
        enableTrackChange = true,
        maxLength,
    }) => {
        const { onReady, onBlur, onChange } = handlers || {}
        const requestHighlight = useHighlightStoreShallowSelector(
            (s) => s.requestHighlight
        )
        const { isArrowNavigationActive, isSaveOperationInProgress } = useNodeMapShallowSelector(
            (state) => ({
                isArrowNavigationActive: state.isArrowNavigationActive,
                isSaveOperationInProgress: state.isSaveOperationInProgress,
            })
        )
        const { setIsArrowNavigationActive } = useNodeMapActions()
        const { handlePaste } = useHtmlParagraphPaste()
        const { positionCursorAtCoordinates } = useCursorPositioning()
        const hasInitiallyHighlighted = useRef(false)
        const isFirefox = useRef(
            navigator.userAgent.toLowerCase().includes('firefox')
        )
        const chatUser = useChatProfileStore((state) => state.chatUser)
        const suggestionId = useMemo(
            () => initialSuggestions?.[0]?.data?.key as string,
            [initialSuggestions]
        )
        const editorConfig = useMemo(
            () => ({
                licenseKey: processEnv.REACT_APP_CKEditorLicenseKey,
                suggestions: initialSuggestions,
                plugins: EDITOR_PLUGINS,
                me: {
                    id: chatUser?.id ?? '',
                    name: `${chatUser?.profileInfo?.firstName ?? ''}${chatUser?.profileInfo?.lastName ?? ''
                        }`.toUpperCase(),
                },
                comments: COMMENTS_CONFIG,
            }),
            [
                initialSuggestions,
                chatUser?.id,
                chatUser?.profileInfo?.firstName,
                chatUser?.profileInfo?.lastName,
            ]
        )

        /**
         * Sets up core editor plugins and features.
         */
        const setupEditorPlugins = useCallback(
            (editor: ClassicEditor) => {
                if (enableTrackChange) {
                    editor.execute('trackChanges')
                }

                // Prevent user from adding text before/after block-level widgets like tables
                editor.plugins
                    .get('WidgetTypeAround')
                    ?.forceDisabled('OurApplication')

                // Development inspector for debugging
                if (processEnv?.REACT_APP_CKEditorInspector === '1') {
                    import('@ckeditor/ckeditor5-inspector').then(
                        ({ default: CKEditorInspector }) => {
                            CKEditorInspector.attach(editor)
                        }
                    )
                }
            },
            [enableTrackChange]
        )

        /**
         * Sets up document event listeners for paste and focus handling.
         */
        const setupEventListeners = useCallback(
            (editor: ClassicEditor) => {
                // Register paste handler for HTML paragraphs
                editor.editing.view.document.on('paste', handlePaste)

                // Focus listener for highlight management
                editor.editing.view.document.on('focus', () => {
                    hideInLineTrackTip()
                    if (!isFirefox.current && suggestionId) {
                        requestHighlight(suggestionId, HIGHLIGHT_CLASS)
                    }
                })
            },
            [handlePaste, suggestionId, requestHighlight]
        )

        /**
         * Handles Firefox-specific behavior for highlighting and blur events.
         */
        const setupFirefoxSpecifics = useCallback(
            (editor: ClassicEditor) => {
                if (!isFirefox.current) return

                // Firefox-specific highlighting behavior
                if (!hasInitiallyHighlighted.current && suggestionId) {
                    hasInitiallyHighlighted.current = true
                    setTimeout(() => {
                        requestHighlight(suggestionId, HIGHLIGHT_CLASS)
                    }, FIREFOX_HIGHLIGHT_DELAY)
                }

                // Firefox-specific blur handler
                if (onBlur) {
                    editor.editing.view.document.on('blur', (event) => {
                        onBlur(event, editor)
                    })
                }
            },
            [suggestionId, requestHighlight, onBlur]
        )

        /**
         * Handles cursor positioning based on click coordinates.
         * @param editor - The CKEditor instance
         * @returns boolean - true if cursor was positioned, otherwise set focus on the end
         */
        const setupCursorPositioning = useCallback(
            (editor: ClassicEditor): boolean => {
                if (
                    !clickCoordinates ||
                    !positionCursorAtCoordinates(editor, clickCoordinates)
                ) {
                    // Position cursor at the end of content for track changes and focus scenarios
                    editor.model?.change?.((writer) => {
                        const root = editor.model.document.getRoot()
                        if (root) {
                            const range = writer.createRangeIn(root)
                            const position = range.end
                            writer.setSelection(position)
                        }
                    })
                    editor.editing?.view?.focus?.()
                }
                return true
            },
            [clickCoordinates, positionCursorAtCoordinates]
        )

        /**
         * Configures the editor with all necessary settings and event listeners.
         * Called after initial focus to avoid affecting cursor positioning.
         */
        const configureEditor = useCallback(
            (editor: ClassicEditor) => {
                setupFirefoxSpecifics(editor)
                setupCursorPositioning(editor)
            },
            [setupFirefoxSpecifics, setupCursorPositioning]
        )

        /**
         * Handler for when the editor is ready.
         * CRITICAL: Enables track changes IMMEDIATELY to prevent untracked edits during race conditions.
         */
        const onReadyHandler = useCallback(
            async (editor: ClassicEditor) => {
                if (enableTrackChange) {
                    setupEditorPlugins(editor)
                }

                // Set up character limit if specified
                if (maxLength !== undefined) {
                    setupEditorCharacterLimit(editor, maxLength)
                }

                onReady?.(editor)

                // Auto-focus for empty content or arrow navigation to show the caret
                const data = editor.getData()
                if (
                    !data ||
                    data === '<p></p>' ||
                    data.trim() === '' ||
                    isArrowNavigationActive
                ) {
                    editor.editing.view.focus()

                    // Reset arrow navigation flag after focusing editor
                    if (isArrowNavigationActive) {
                        setIsArrowNavigationActive(false)
                    }
                }

                setupEventListeners(editor)

                setTimeout(() => configureEditor(editor), EDITOR_CONFIG_DELAY)
            },
            [
                enableTrackChange,
                maxLength,
                onReady,
                configureEditor,
                setupEventListeners,
                setupEditorPlugins,
                isArrowNavigationActive,
                setIsArrowNavigationActive,
            ]
        )


        return (
            <ContentEditorLayout>
                <CKEditor
                    data-testid="content-editor"
                    editor={ClassicEditor}
                    config={editorConfig}
                    data={initialData}
                    onReady={onReadyHandler}
                    onBlur={onBlur}
                    onChange={onChange}
                    disabled={isSaveOperationInProgress}
                />
            </ContentEditorLayout>
        )
    }
)
